import {
  GltfMemoryBudgetExceededError,
  GltfRangeRequestError,
  GltfReferenceOutOfBoundsError,
  MeshLoadCancelledError,
} from '@/plugins/mesh/errors'
import { decodeDataUri } from '@/plugins/mesh/loaders/gltfLowMemory/decodeDataUri'
import type {
  GltfBufferSource,
  GltfBufferSourcePerformanceReport,
} from '@/plugins/mesh/loaders/gltfLowMemory/interfaces'
import type { GltfMemoryBudgetOptions, GltfRangeLoadingOptions } from '@/plugins/mesh/types'
import { getLoggerManager } from '@/shared/logging'

const clock = getLoggerManager()

interface RangeCacheEntry {
  readonly start: number
  readonly end: number
  readonly bytes: Uint8Array
  lastUsed: number
}

interface QueuedRead {
  readonly start: number
  readonly end: number
  readonly key: string
  readonly signal?: AbortSignal
  readonly resolve: (bytes: Uint8Array) => void
  readonly reject: (error: unknown) => void
}

interface MergedRead {
  readonly start: number
  readonly end: number
  readonly reads: readonly QueuedRead[]
  readonly queuedAt: number
}

interface ResponseBytesResult {
  readonly bytes: Uint8Array
  readonly bodyReadMs: number
  readonly bodyCopyMs: number
}

interface Share3dServerTiming {
  statMs?: number
  openMs?: number
  readMs?: number
  closeMs?: number
  totalMs?: number
}

interface UrlGltfBufferSourceOptions {
  readonly url: string
  readonly byteLength: number
  readonly baseOffset?: number
  readonly headers?: HeadersInit
  readonly credentials?: RequestCredentials
  readonly range: GltfRangeLoadingOptions
  readonly budget: GltfMemoryBudgetOptions
  readonly onProgress?: (progress: number) => void
  readonly diagnosticsEnabled?: boolean
}

function assertReadBounds(
  offset: number,
  length: number,
  byteLength: number,
  source: string
): void {
  if (
    !Number.isInteger(offset) ||
    !Number.isInteger(length) ||
    offset < 0 ||
    length < 0 ||
    offset + length > byteLength
  ) {
    throw new GltfReferenceOutOfBoundsError(
      source,
      `读取范围 offset=${offset}, length=${length}, byteLength=${byteLength}`
    )
  }
}

function throwIfCancelled(signal: AbortSignal | undefined, source: string): void {
  if (signal?.aborted) {
    throw new MeshLoadCancelledError(source, { cause: signal.reason })
  }
}

function parseShare3dServerTiming(header: string | null): Share3dServerTiming | undefined {
  if (!header) return undefined
  const result: Share3dServerTiming = {}
  for (const metric of header.split(',')) {
    const [rawName, ...parameters] = metric.split(';')
    const durationParameter = parameters.find((parameter) => /^\s*dur\s*=/i.test(parameter))
    if (!durationParameter) continue
    const rawDuration = durationParameter.slice(durationParameter.indexOf('=') + 1).trim()
    const duration = Number(rawDuration.replace(/^"|"$/g, ''))
    if (!Number.isFinite(duration) || duration < 0) continue
    switch (rawName.trim().toLowerCase()) {
      case 'share3d-stat':
        result.statMs = duration
        break
      case 'share3d-open':
        result.openMs = duration
        break
      case 'share3d-read':
        result.readMs = duration
        break
      case 'share3d-close':
        result.closeMs = duration
        break
      case 'share3d-total':
        result.totalMs = duration
        break
    }
  }
  return Object.keys(result).length > 0 ? result : undefined
}

async function readResponseBytes(
  response: Response,
  signal: AbortSignal | undefined,
  source: string,
  onProgress?: (progress: number) => void
): Promise<ResponseBytesResult> {
  const reader = response.body?.getReader()
  const total = Number(response.headers.get('content-length')) || 0
  if (!reader) {
    const bodyStartedAt = clock.now()
    const bytes = new Uint8Array(await response.arrayBuffer())
    throwIfCancelled(signal, source)
    onProgress?.(1)
    return { bytes, bodyReadMs: clock.now() - bodyStartedAt, bodyCopyMs: 0 }
  }

  let completed = false
  try {
    const chunks: Uint8Array[] = []
    let loaded = 0
    const bodyStartedAt = clock.now()
    while (true) {
      throwIfCancelled(signal, source)
      const { done, value } = await reader.read()
      if (done) {
        completed = true
        break
      }
      chunks.push(value)
      loaded += value.byteLength
      onProgress?.(total > 0 ? loaded / total : 0)
    }
    const bodyReadMs = clock.now() - bodyStartedAt
    const copyStartedAt = clock.now()
    const bytes = new Uint8Array(loaded)
    let offset = 0
    for (const chunk of chunks) {
      bytes.set(chunk, offset)
      offset += chunk.byteLength
    }
    const bodyCopyMs = clock.now() - copyStartedAt
    onProgress?.(1)
    return { bytes, bodyReadMs, bodyCopyMs }
  } finally {
    if (!completed) {
      try {
        await reader.cancel(signal?.reason)
      } catch {
        // 响应体可能已经进入错误状态。
      }
    }
  }
}

function percentile(values: readonly number[], ratio: number): number {
  if (values.length === 0) return 0
  const sorted = [...values].sort((left, right) => left - right)
  return sorted[Math.min(sorted.length - 1, Math.floor((sorted.length - 1) * ratio))] ?? 0
}

function inferSourceKind(url: string): GltfBufferSourcePerformanceReport['sourceKind'] {
  try {
    const protocol = new URL(url, globalThis.location?.href).protocol
    return protocol === 'http:' || protocol === 'https:' ? 'http' : 'other-url'
  } catch {
    return 'other-url'
  }
}

/** 不转移调用方所有权的 ArrayBuffer/ArrayBufferView 边界来源。 */
export class MemoryGltfBufferSource implements GltfBufferSource {
  readonly peakCacheBytes = 0
  readonly downloadedBytes = 0
  readonly byteLength: number

  private readonly bytes: Uint8Array

  constructor(
    data: ArrayBuffer | ArrayBufferView,
    private readonly baseOffset = 0,
    byteLength?: number,
    private readonly source = '(内存 Buffer)'
  ) {
    const sourceBytes = ArrayBuffer.isView(data)
      ? new Uint8Array(data.buffer, data.byteOffset, data.byteLength)
      : new Uint8Array(data)
    this.byteLength = byteLength ?? sourceBytes.byteLength - baseOffset
    assertReadBounds(baseOffset, this.byteLength, sourceBytes.byteLength, source)
    this.bytes = sourceBytes
  }

  async read(offset: number, length: number, signal?: AbortSignal): Promise<Uint8Array> {
    throwIfCancelled(signal, this.source)
    assertReadBounds(offset, length, this.byteLength, this.source)
    return this.bytes.slice(this.baseOffset + offset, this.baseOffset + offset + length)
  }

  dispose(): void {}
}

/** Blob.slice() 驱动的所有权安全 BufferSource。 */
export class BlobGltfBufferSource implements GltfBufferSource {
  readonly peakCacheBytes = 0
  readonly downloadedBytes = 0
  readonly byteLength: number

  constructor(
    private readonly blob: Blob,
    private readonly baseOffset = 0,
    byteLength?: number,
    private readonly source = '(Blob Buffer)'
  ) {
    this.byteLength = byteLength ?? blob.size - baseOffset
    assertReadBounds(baseOffset, this.byteLength, blob.size, source)
  }

  async read(offset: number, length: number, signal?: AbortSignal): Promise<Uint8Array> {
    throwIfCancelled(signal, this.source)
    assertReadBounds(offset, length, this.byteLength, this.source)
    const bytes = new Uint8Array(
      await this.blob
        .slice(this.baseOffset + offset, this.baseOffset + offset + length)
        .arrayBuffer()
    )
    throwIfCancelled(signal, this.source)
    return bytes
  }

  dispose(): void {}
}

/** 惰性解码且按边界返回副本的 data URI BufferSource。 */
export class DataUriGltfBufferSource implements GltfBufferSource {
  readonly downloadedBytes = 0
  readonly byteLength: number

  private bytes?: Uint8Array
  private _peakCacheBytes = 0

  constructor(
    private readonly uri: string,
    declaredByteLength: number
  ) {
    this.byteLength = declaredByteLength
  }

  get peakCacheBytes(): number {
    return this._peakCacheBytes
  }

  async read(offset: number, length: number, signal?: AbortSignal): Promise<Uint8Array> {
    throwIfCancelled(signal, this.uri)
    assertReadBounds(offset, length, this.byteLength, this.uri)
    this.bytes ??= decodeDataUri(this.uri).bytes
    this._peakCacheBytes = Math.max(this._peakCacheBytes, this.bytes.byteLength)
    if (this.bytes.byteLength < this.byteLength) {
      throw new GltfReferenceOutOfBoundsError(
        this.uri,
        `data URI 实际 ${this.bytes.byteLength} 字节，小于声明 ${this.byteLength}`
      )
    }
    return this.bytes.slice(offset, offset + length)
  }

  dispose(): void {
    this.bytes = undefined
  }
}

/** 支持 Range 合并、并发限制、请求去重和字节 LRU 的 URL BufferSource。 */
export class UrlGltfBufferSource implements GltfBufferSource {
  readonly byteLength: number

  private readonly url: string
  private readonly baseOffset: number
  private readonly headers?: HeadersInit
  private readonly credentials?: RequestCredentials
  private readonly range: GltfRangeLoadingOptions
  private readonly budget: GltfMemoryBudgetOptions
  private readonly onProgress?: (progress: number) => void
  private readonly cache: RangeCacheEntry[] = []
  private readonly inflight = new Map<string, Promise<Uint8Array>>()
  private readonly controllers = new Set<AbortController>()
  private readonly mergedQueue: MergedRead[] = []
  private queuedReads: QueuedRead[] = []
  private cacheBytes = 0
  private accessClock = 0
  private activeRequests = 0
  private flushScheduled = false
  private disposed = false
  private fullResponse?: Uint8Array
  private fullInflight?: Promise<void>
  private rangeCapability: 'unknown' | 'supported' | 'unsupported' = 'unknown'
  private _peakCacheBytes = 0
  private _downloadedBytes = 0
  private logicalReadCount = 0
  private logicalReadBytes = 0
  private cacheHitCount = 0
  private cacheHitBytes = 0
  private inflightHitCount = 0
  private inflightHitBytes = 0
  private rangeRequestCount = 0
  private fullRequestCount = 0
  private rangeResponseBytes = 0
  private fullResponseBytes = 0
  private uniqueRangeBytes = 0
  private networkMs = 0
  private peakConcurrentRequests = 0
  private peakQueueDepth = 0
  private queueWaitMs = 0
  private responseWaitMs = 0
  private bodyReadMs = 0
  private bodyCopyMs = 0
  private serverTimingCount = 0
  private serverTimingTotalMs = 0
  private serverTimingStatMs = 0
  private serverTimingOpenMs = 0
  private serverTimingReadMs = 0
  private serverTimingCloseMs = 0
  private readonly queueWaitSamples: number[] = []
  private readonly responseWaitSamples: number[] = []
  private readonly bodyReadSamples: number[] = []
  private readonly bodyCopySamples: number[] = []
  private readonly requestByteSamples: number[] = []
  private readonly serverTimingTotalSamples: number[] = []
  private readonly sourceKinds = new Set<GltfBufferSourcePerformanceReport['sourceKind']>()
  private readonly downloadedRanges: { start: number; end: number }[] = []
  private readonly diagnosticsEnabled: boolean

  constructor(options: UrlGltfBufferSourceOptions) {
    this.url = options.url
    this.byteLength = options.byteLength
    this.baseOffset = options.baseOffset ?? 0
    this.headers = options.headers
    this.credentials = options.credentials
    this.range = options.range
    this.budget = options.budget
    this.onProgress = options.onProgress
    this.diagnosticsEnabled = options.diagnosticsEnabled ?? false
  }

  get peakCacheBytes(): number {
    return this._peakCacheBytes
  }

  get downloadedBytes(): number {
    return this._downloadedBytes
  }

  getPerformanceReport(): GltfBufferSourcePerformanceReport {
    const sourceKind =
      this.sourceKinds.size === 0
        ? inferSourceKind(this.url)
        : this.sourceKinds.size === 1
          ? [...this.sourceKinds][0]
          : 'mixed'
    return {
      sourceKind,
      logicalReadCount: this.logicalReadCount,
      logicalReadBytes: this.logicalReadBytes,
      cacheHitCount: this.cacheHitCount,
      cacheHitBytes: this.cacheHitBytes,
      inflightHitCount: this.inflightHitCount,
      inflightHitBytes: this.inflightHitBytes,
      rangeRequestCount: this.rangeRequestCount,
      fullRequestCount: this.fullRequestCount,
      rangeResponseBytes: this.rangeResponseBytes,
      fullResponseBytes: this.fullResponseBytes,
      uniqueRangeBytes: this.uniqueRangeBytes,
      overlapRangeBytes: Math.max(0, this.rangeResponseBytes - this.uniqueRangeBytes),
      configuredConcurrency: this.range.maxConcurrentRequests,
      peakConcurrentRequests: this.peakConcurrentRequests,
      peakQueueDepth: this.peakQueueDepth,
      queueWaitMs: this.queueWaitMs,
      queueWaitP95Ms: percentile(this.queueWaitSamples, 0.95),
      queueWaitMaxMs: Math.max(0, ...this.queueWaitSamples),
      responseWaitMs: this.responseWaitMs,
      responseWaitP95Ms: percentile(this.responseWaitSamples, 0.95),
      responseWaitMaxMs: Math.max(0, ...this.responseWaitSamples),
      bodyReadMs: this.bodyReadMs,
      bodyReadP95Ms: percentile(this.bodyReadSamples, 0.95),
      bodyReadMaxMs: Math.max(0, ...this.bodyReadSamples),
      bodyCopyMs: this.bodyCopyMs,
      bodyCopyMaxMs: Math.max(0, ...this.bodyCopySamples),
      requestBytesP50: percentile(this.requestByteSamples, 0.5),
      requestBytesP95: percentile(this.requestByteSamples, 0.95),
      requestBytesMax: Math.max(0, ...this.requestByteSamples),
      serverTimingCount: this.serverTimingCount,
      serverTimingTotalMs: this.serverTimingTotalMs,
      serverTimingTotalP95Ms: percentile(this.serverTimingTotalSamples, 0.95),
      serverTimingTotalMaxMs: Math.max(0, ...this.serverTimingTotalSamples),
      serverTimingStatMs: this.serverTimingStatMs,
      serverTimingOpenMs: this.serverTimingOpenMs,
      serverTimingReadMs: this.serverTimingReadMs,
      serverTimingCloseMs: this.serverTimingCloseMs,
      networkMs: this.networkMs,
    }
  }

  async read(offset: number, length: number, signal?: AbortSignal): Promise<Uint8Array> {
    this.ensureActive()
    throwIfCancelled(signal, this.url)
    assertReadBounds(offset, length, this.byteLength, this.url)
    if (length === 0) return new Uint8Array()
    this.logicalReadCount += 1
    this.logicalReadBytes += length
    if (this.fullResponse) {
      this.cacheHitCount += 1
      this.cacheHitBytes += length
      return this.fullResponse.slice(this.baseOffset + offset, this.baseOffset + offset + length)
    }
    const cached = this.findCached(offset, offset + length)
    if (cached) {
      this.cacheHitCount += 1
      this.cacheHitBytes += length
      return cached
    }
    if (!this.range.enabled) {
      await this.loadFull(signal)
      return this.fullResponse!.slice(this.baseOffset + offset, this.baseOffset + offset + length)
    }
    return this.queueRangeRead(offset, length, signal)
  }

  dispose(): void {
    if (this.disposed) return
    this.disposed = true
    for (const controller of this.controllers) controller.abort('buffer source disposed')
    this.controllers.clear()
    const error = new MeshLoadCancelledError(this.url)
    for (const read of this.queuedReads) read.reject(error)
    for (const merged of this.mergedQueue) {
      for (const read of merged.reads) read.reject(error)
    }
    this.queuedReads = []
    this.mergedQueue.length = 0
    this.cache.length = 0
    this.cacheBytes = 0
    this.fullResponse = undefined
    this.fullInflight = undefined
    this.inflight.clear()
  }

  private queueRangeRead(
    offset: number,
    length: number,
    signal?: AbortSignal
  ): Promise<Uint8Array> {
    const key = `${offset}:${length}`
    const existing = this.inflight.get(key)
    if (existing) {
      this.inflightHitCount += 1
      this.inflightHitBytes += length
      return existing.then((bytes) => bytes.slice())
    }

    const promise = new Promise<Uint8Array>((resolve, reject) => {
      this.queuedReads.push({
        start: offset,
        end: offset + length,
        key,
        signal,
        resolve,
        reject,
      })
      if (!this.flushScheduled) {
        this.flushScheduled = true
        queueMicrotask(() => this.flushQueuedReads())
      }
    })
    this.inflight.set(key, promise)
    promise.then(
      () => this.inflight.delete(key),
      () => this.inflight.delete(key)
    )
    return promise.then((bytes) => bytes.slice())
  }

  private flushQueuedReads(): void {
    this.flushScheduled = false
    const reads = this.queuedReads
      .splice(0)
      .filter((read) => {
        if (!read.signal?.aborted) return true
        read.reject(new MeshLoadCancelledError(this.url, { cause: read.signal.reason }))
        return false
      })
      .sort((left, right) => left.start - right.start)
    let current: QueuedRead[] = []
    let start = 0
    let end = 0
    const pushCurrent = () => {
      if (current.length === 0) return
      this.mergedQueue.push({ start, end, reads: current, queuedAt: clock.now() })
      this.peakQueueDepth = Math.max(this.peakQueueDepth, this.mergedQueue.length)
      current = []
    }
    for (const read of reads) {
      const canMerge =
        current.length > 0 &&
        read.start <= end + this.range.mergeGapBytes &&
        Math.max(end, read.end) - start <= this.range.maxRequestBytes
      if (!canMerge) {
        pushCurrent()
        start = read.start
        end = read.end
        current = [read]
      } else {
        end = Math.max(end, read.end)
        current.push(read)
      }
    }
    pushCurrent()
    this.pumpQueue()
  }

  private pumpQueue(): void {
    const concurrency = this.rangeCapability === 'unknown' ? 1 : this.range.maxConcurrentRequests
    while (this.activeRequests < concurrency && this.mergedQueue.length > 0) {
      const merged = this.mergedQueue.shift()!
      this.activeRequests += 1
      this.peakConcurrentRequests = Math.max(this.peakConcurrentRequests, this.activeRequests)
      const queueWaitMs = clock.now() - merged.queuedAt
      this.queueWaitMs += queueWaitMs
      if (this.diagnosticsEnabled) this.queueWaitSamples.push(queueWaitMs)
      this.executeMergedRead(merged)
        .catch(() => {
          // executeMergedRead 已向全部等待方报告错误。
        })
        .finally(() => {
          this.activeRequests -= 1
          this.pumpQueue()
        })
    }
  }

  private async executeMergedRead(merged: MergedRead): Promise<void> {
    const controller = new AbortController()
    this.controllers.add(controller)
    const removers: (() => void)[] = []
    for (const read of merged.reads) {
      const abort = () => controller.abort(read.signal?.reason)
      if (read.signal?.aborted) abort()
      else read.signal?.addEventListener('abort', abort, { once: true })
      removers.push(() => read.signal?.removeEventListener('abort', abort))
    }
    try {
      const bytes = await this.fetchRange(merged.start, merged.end, controller.signal)
      throwIfCancelled(controller.signal, this.url)
      if (!this.fullResponse) this.addCache(merged.start, merged.end, bytes)
      for (const read of merged.reads) {
        throwIfCancelled(read.signal, this.url)
        const value = this.fullResponse
          ? this.fullResponse.slice(this.baseOffset + read.start, this.baseOffset + read.end)
          : bytes.slice(read.start - merged.start, read.end - merged.start)
        read.resolve(value)
      }
    } catch (error) {
      for (const read of merged.reads) {
        read.reject(
          controller.signal.aborted
            ? new MeshLoadCancelledError(this.url, {
                cause: controller.signal.reason ?? error,
              })
            : error
        )
      }
      throw error
    } finally {
      for (const remove of removers) remove()
      this.controllers.delete(controller)
    }
  }

  private async fetchRange(start: number, end: number, signal: AbortSignal): Promise<Uint8Array> {
    const networkStartedAt = clock.now()
    this.rangeRequestCount += 1
    let networkRecorded = false
    const recordNetworkTime = () => {
      if (networkRecorded) return
      networkRecorded = true
      this.networkMs += clock.now() - networkStartedAt
    }
    try {
      const absoluteStart = this.baseOffset + start
      const absoluteEnd = this.baseOffset + end - 1
      const headers = new Headers(this.headers)
      headers.set('Range', `bytes=${absoluteStart}-${absoluteEnd}`)
      let response: Response
      try {
        const responseStartedAt = clock.now()
        response = await fetch(this.url, {
          signal,
          headers,
          credentials: this.credentials,
        })
        this.recordResponseWait(clock.now() - responseStartedAt)
        this.recordResponseMetadata(response)
      } catch (error) {
        recordNetworkTime()
        throwIfCancelled(signal, this.url)
        if (!this.range.fallbackToFullBuffer) {
          throw new GltfRangeRequestError(this.url, 'range-unavailable', 'Range 请求失败', error)
        }
        await this.loadFull(signal)
        return this.fullResponse!.slice(this.baseOffset + start, this.baseOffset + end)
      }

      if (response.status === 200) {
        if (!this.range.fallbackToFullBuffer) {
          recordNetworkTime()
          throw new GltfRangeRequestError(this.url, 'range-unavailable', '服务器忽略了 Range 请求')
        }
        let result: ResponseBytesResult
        try {
          result = await readResponseBytes(response, signal, this.url, this.onProgress)
        } catch {
          recordNetworkTime()
          throwIfCancelled(signal, this.url)
          await this.loadFull(signal)
          return this.fullResponse!.slice(this.baseOffset + start, this.baseOffset + end)
        }
        const { bytes } = result
        this.recordResponseBytes(result)
        recordNetworkTime()
        this._downloadedBytes += bytes.byteLength
        this.fullResponseBytes += bytes.byteLength
        this.rangeCapability = 'unsupported'
        this.acceptFullResponse(bytes)
        return this.fullResponse!.slice(this.baseOffset + start, this.baseOffset + end)
      }
      if (response.status !== 206) {
        recordNetworkTime()
        if (!this.range.fallbackToFullBuffer) {
          throw new GltfRangeRequestError(
            this.url,
            'range-unavailable',
            `Range 状态码 ${response.status}`
          )
        }
        this.rangeCapability = 'unsupported'
        await this.loadFull(signal)
        return this.fullResponse!.slice(this.baseOffset + start, this.baseOffset + end)
      }
      const contentRange = response.headers.get('content-range')
      const expected = `bytes ${absoluteStart}-${absoluteEnd}/`
      if (!contentRange?.startsWith(expected)) {
        recordNetworkTime()
        throw new GltfRangeRequestError(
          this.url,
          'range-invalid-response',
          `Content-Range 应以 "${expected}" 开头，收到 "${contentRange ?? '(缺失)'}"`
        )
      }
      this.rangeCapability = 'supported'
      let result: ResponseBytesResult
      try {
        result = await readResponseBytes(response, signal, this.url, this.onProgress)
      } catch (error) {
        recordNetworkTime()
        throwIfCancelled(signal, this.url)
        if (!this.range.fallbackToFullBuffer) throw error
        await this.loadFull(signal)
        return this.fullResponse!.slice(this.baseOffset + start, this.baseOffset + end)
      }
      const { bytes } = result
      this.recordResponseBytes(result)
      recordNetworkTime()
      this._downloadedBytes += bytes.byteLength
      this.rangeResponseBytes += bytes.byteLength
      if (bytes.byteLength !== end - start) {
        if (this.range.fallbackToFullBuffer) {
          await this.loadFull(signal)
          return this.fullResponse!.slice(this.baseOffset + start, this.baseOffset + end)
        }
        throw new GltfRangeRequestError(
          this.url,
          'range-invalid-response',
          `Range 响应长度 ${bytes.byteLength}，预期 ${end - start}`
        )
      }
      this.recordDownloadedRange(start, end)
      return bytes
    } finally {
      recordNetworkTime()
    }
  }

  private async loadFull(signal?: AbortSignal): Promise<void> {
    if (this.fullResponse) return
    this.fullInflight ??= this.loadFullOnce().finally(() => {
      this.fullInflight = undefined
    })
    await this.waitForFullResponse(this.fullInflight, signal)
  }

  private async loadFullOnce(): Promise<void> {
    const controller = new AbortController()
    this.controllers.add(controller)
    const networkStartedAt = clock.now()
    this.fullRequestCount += 1
    try {
      const responseStartedAt = clock.now()
      const response = await fetch(this.url, {
        signal: controller.signal,
        headers: this.headers,
        credentials: this.credentials,
      })
      this.recordResponseWait(clock.now() - responseStartedAt)
      this.recordResponseMetadata(response)
      if (!response.ok) {
        throw new GltfRangeRequestError(
          this.url,
          'range-unavailable',
          `完整 Buffer 请求失败: ${response.status}`
        )
      }
      const result = await readResponseBytes(response, controller.signal, this.url, this.onProgress)
      const { bytes } = result
      this.recordResponseBytes(result)
      this._downloadedBytes += bytes.byteLength
      this.fullResponseBytes += bytes.byteLength
      this.acceptFullResponse(bytes)
    } catch (error) {
      throwIfCancelled(controller.signal, this.url)
      throw error
    } finally {
      this.networkMs += clock.now() - networkStartedAt
      this.controllers.delete(controller)
    }
  }

  private recordResponseWait(durationMs: number): void {
    this.responseWaitMs += durationMs
    if (this.diagnosticsEnabled) this.responseWaitSamples.push(durationMs)
  }

  private recordResponseBytes(result: ResponseBytesResult): void {
    this.bodyReadMs += result.bodyReadMs
    this.bodyCopyMs += result.bodyCopyMs
    if (this.diagnosticsEnabled) {
      this.bodyReadSamples.push(result.bodyReadMs)
      this.bodyCopySamples.push(result.bodyCopyMs)
      this.requestByteSamples.push(result.bytes.byteLength)
    }
  }

  private recordResponseMetadata(response: Response): void {
    const explicit = response.headers.get('x-share3d-source-kind')
    if (explicit === 'local-file') {
      this.sourceKinds.add('local-file')
    } else {
      this.sourceKinds.add(inferSourceKind(response.url || this.url))
    }
    const serverTiming = parseShare3dServerTiming(response.headers.get('server-timing'))
    if (!serverTiming) return
    this.serverTimingCount += 1
    this.serverTimingStatMs += serverTiming.statMs ?? 0
    this.serverTimingOpenMs += serverTiming.openMs ?? 0
    this.serverTimingReadMs += serverTiming.readMs ?? 0
    this.serverTimingCloseMs += serverTiming.closeMs ?? 0
    if (serverTiming.totalMs === undefined) return
    this.serverTimingTotalMs += serverTiming.totalMs
    if (this.diagnosticsEnabled) this.serverTimingTotalSamples.push(serverTiming.totalMs)
  }

  private recordDownloadedRange(start: number, end: number): void {
    if (!this.diagnosticsEnabled) return
    let mergedStart = start
    let mergedEnd = end
    let removedBytes = 0
    for (let index = this.downloadedRanges.length - 1; index >= 0; index -= 1) {
      const range = this.downloadedRanges[index]
      if (range.end < mergedStart || range.start > mergedEnd) continue
      mergedStart = Math.min(mergedStart, range.start)
      mergedEnd = Math.max(mergedEnd, range.end)
      removedBytes += range.end - range.start
      this.downloadedRanges.splice(index, 1)
    }
    this.downloadedRanges.push({ start: mergedStart, end: mergedEnd })
    this.uniqueRangeBytes += mergedEnd - mergedStart - removedBytes
  }

  private waitForFullResponse(promise: Promise<void>, signal?: AbortSignal): Promise<void> {
    throwIfCancelled(signal, this.url)
    if (!signal) return promise
    return new Promise<void>((resolve, reject) => {
      const handleAbort = () => {
        reject(new MeshLoadCancelledError(this.url, { cause: signal.reason }))
      }
      signal.addEventListener('abort', handleAbort, { once: true })
      promise.then(resolve, reject).finally(() => {
        signal.removeEventListener('abort', handleAbort)
      })
    })
  }

  private acceptFullResponse(bytes: Uint8Array): void {
    if (bytes.byteLength > this.budget.maxTransientBytes) {
      throw new GltfMemoryBudgetExceededError(
        this.url,
        'full-buffer-budget-exceeded',
        bytes.byteLength,
        this.budget.maxTransientBytes
      )
    }
    if (this.baseOffset + this.byteLength > bytes.byteLength) {
      throw new GltfReferenceOutOfBoundsError(
        this.url,
        `完整响应 ${bytes.byteLength} 字节不足以覆盖 Buffer 结束位置 ${
          this.baseOffset + this.byteLength
        }`
      )
    }
    this._peakCacheBytes = Math.max(this._peakCacheBytes, this.cacheBytes + bytes.byteLength)
    this.rangeCapability = 'unsupported'
    this.fullResponse = bytes
    this.cache.length = 0
    this.cacheBytes = 0
  }

  private findCached(start: number, end: number): Uint8Array | undefined {
    const entry = this.cache.find((candidate) => candidate.start <= start && candidate.end >= end)
    if (!entry) return undefined
    entry.lastUsed = ++this.accessClock
    return entry.bytes.slice(start - entry.start, end - entry.start)
  }

  private addCache(start: number, end: number, bytes: Uint8Array): void {
    if (bytes.byteLength > this.budget.maxRangeCacheBytes) return
    for (let index = this.cache.length - 1; index >= 0; index -= 1) {
      const entry = this.cache[index]
      if (entry.end <= start || entry.start >= end) continue
      this.cache.splice(index, 1)
      this.cacheBytes -= entry.bytes.byteLength
    }
    while (
      this.cacheBytes + bytes.byteLength > this.budget.maxRangeCacheBytes &&
      this.cache.length > 0
    ) {
      let oldestIndex = 0
      for (let index = 1; index < this.cache.length; index += 1) {
        if (this.cache[index].lastUsed < this.cache[oldestIndex].lastUsed) oldestIndex = index
      }
      const [removed] = this.cache.splice(oldestIndex, 1)
      this.cacheBytes -= removed.bytes.byteLength
    }
    this.cache.push({
      start,
      end,
      bytes,
      lastUsed: ++this.accessClock,
    })
    this.cacheBytes += bytes.byteLength
    this._peakCacheBytes = Math.max(this._peakCacheBytes, this.cacheBytes)
  }

  private ensureActive(): void {
    if (this.disposed) throw new MeshLoadCancelledError(this.url)
  }
}
